import { setPersistedOrgId, useAuth0 } from "@appretio/auth";
import { type ResolvedOrg, resolveOrgForEmail } from "@appretio/shared";
import { Button, InputField, Logo } from "@appretio/ui-kit";
import { FormEvent, ReactNode, useState } from "react";
import * as styles from "./LoginScreen.css";

/**
 * Pre-login tenant discovery screen.
 *
 * The SPA must send an `organization` to Auth0 before it can log in
 * (organization_usage=require), so on a first login (no persisted tenant) it
 * collects the user's email and resolves it to their org(s) via the backend.
 * One org → redirect straight to Auth0 scoped to it. Several (a shared domain
 * with no prior membership, e.g. bestseller.com) → a one-time org picker; a
 * wrong pick needs no special handling here, the AuthGuard callback-error
 * recovery clears it and returns the user to this screen. Rendered by
 * `AuthGuard`.
 */
export function LoginScreen() {
  const { loginWithRedirect } = useAuth0();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [candidates, setCandidates] = useState<ResolvedOrg[]>([]);

  // Persist so the org is supplied on every later (silent) token call, and
  // scope this login to it. login_hint lets Auth0 route to the right connection
  // without a second prompt. A resolved `connection` (super-users) bypasses
  // HRD entirely — it must, since super-users share the bestseller.com domain
  // with normal staff but must enter via appretio-okta.
  async function startLogin(org: ResolvedOrg) {
    setPersistedOrgId(org.id);
    await loginWithRedirect({
      authorizationParams: {
        organization: org.id,
        login_hint: email,
        ...(org.connection ? { connection: org.connection } : {})
      }
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Validated here (form is noValidate) so a failed submit doesn't put the
    // browser in interactive-validation mode, which re-pops the native bubble
    // on every keystroke until the email parses.
    if (!/\S+@\S+/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(null);
    setIsSubmitting(true);

    // Two distinct failure domains, kept separate so the message points at the
    // actual step: (1) reaching the backend to resolve the org, vs (2) handing
    // off to Auth0. Folding both into one catch produced a misleading
    // "lookup failed" even when the lookup succeeded and the redirect was at
    // fault.
    let orgs: ResolvedOrg[];
    try {
      orgs = await resolveOrgForEmail(email);
    } catch {
      setError(
        "Check your internet connection and try again — we couldn't reach the sign-in service to look up your organization."
      );
      setIsSubmitting(false);
      return;
    }

    if (orgs.length === 0) {
      setError(
        "Double-check your email address, or contact your administrator if it's correct — we couldn't find an organization for that email."
      );
      setIsSubmitting(false);
      return;
    }

    if (orgs.length === 1) {
      try {
        await startLogin(orgs[0]);
      } catch {
        setError(
          "Please try again — we found your organization, but couldn't start sign-in."
        );
        setIsSubmitting(false);
      }
      return;
    }

    // Several candidates on a shared domain — let the user choose once.
    setCandidates(orgs);
    setIsSubmitting(false);
  }

  async function handleChoose(org: ResolvedOrg) {
    setError(null);
    setIsSubmitting(true);
    try {
      await startLogin(org);
    } catch {
      setError(
        "Please try again — we found your organization, but couldn't start sign-in."
      );
      setIsSubmitting(false);
    }
  }

  if (candidates.length > 0) {
    return (
      <Page
        title="Choose your workspace"
        subtitle={`More than one workspace uses ${email}. Pick where to sign in.`}
      >
        <div className={styles.form}>
          {candidates.map((org) => (
            <Button
              key={org.id}
              type="button"
              onClick={() => handleChoose(org)}
              disabled={isSubmitting}
            >
              {org.display_name}
            </Button>
          ))}
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Sign in to let the magic begin"
      subtitle="Enter your work email to continue."
    >
      <form
        className={styles.form}
        onSubmit={handleSubmit}
        aria-label="Sign in to Appretio"
        noValidate
      >
        <InputField
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@company.com"
          required
          error={Boolean(error)}
        />
        {error && (
          <p role="alert" className={styles.errorText}>
            {error}
          </p>
        )}
        <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
          Continue
        </Button>
      </form>
    </Page>
  );
}

interface PageProps {
  /** Heading inside the card. */
  title: string;
  /** One-line helper text under the heading. */
  subtitle: string;
  /** Card body — the form or the org picker. */
  children: ReactNode;
}

// Layout revived from the pre-monorepo LoginPage (logo above the card,
// copyright footer) — see LoginScreen.css.ts.
function Page({ title, subtitle, children }: PageProps) {
  return (
    <div className={styles.loginPageBase}>
      <div className={styles.loginMainContent}>
        <div className={styles.loginContentContainer}>
          <div className={styles.logoContainer}>
            <Logo />
          </div>
          <div className={styles.loginFormContainer}>
            <div className={styles.header}>
              <h1 className={styles.headingText}>{title}</h1>
              <p className={styles.subtitle}>{subtitle}</p>
            </div>
            {children}
          </div>
        </div>
      </div>
      <p className={styles.copyrightText}>
        © Appretio {new Date().getFullYear()}
      </p>
    </div>
  );
}
